import React from "react";
import { getDaysUntil } from '../../../utils/daysUntil'
import { formatDate } from '../../../utils/formatDate'
import formatCurrency from "../../../utils/formatCurrency"
import { useIsMobile } from '../../../utils/hooks'

type UpcomingPaymentCardProps = {
    title: string;
    date: string;
    method: string;
    category: string
    amount: number;
    collapsed?: boolean;
    rewards?: string;
    inSubpage: boolean
};

const PayableCard: React.FC<UpcomingPaymentCardProps> = ({
    title,
    date,
    method,
    category,
    amount,
    collapsed = true,
    rewards,
    inSubpage
}) => {
    const icon = (
        <div
            className={`bg-gray-800 shadow flex rounded-full aspect-square w-11 shrink-0 items-center justify-center`}
        >
            <span className={`font-medium text-white text-2xl`}>
                {title[0]}
            </span>
        </div>
    );

    const formattedAmount = formatCurrency(amount)
    const formattedDate = formatDate(date);
    const isMobile = useIsMobile()

    if (isMobile && inSubpage) {
        return (
            <div className="flex flex-col gap-5 w-full rounded-lg border p-5 font-medium">
                <div className="w-full flex justify-between">
                    {/* Title and Details */}
                    <div className="flex gap-2 items-center">
                        {icon}
                        <div className="flex flex-col">
                            <div className="text-gray-800 font-semibold">{title}</div>
                            <div className="flex flex-wrap items-center text-sm text-gray-500 gap-1">
                                <span>{formattedDate} ● </span>
                                <span>{method}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <span>{formattedAmount}</span>
                        <span className="text-gray-500 text-sm">Due in {getDaysUntil(date)} days</span>
                    </div>
                </div>
                <div className="w-full flex gap-2 grid grid-cols-[1fr_1fr_1.5fr]">
                    <div className="flex flex-col">
                        <span className="text-gray-500 mb-1">Account</span>
                        <span>-</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-500 mb-1">Category</span>
                        <span>{category}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-500 mb-1">Tags or Remarks</span>
                        <span className="tag-1 w-fit">{category}</span>
                    </div>
                </div>
            </div>
        )
    }

    return (
        collapsed ? (
            <div className="text-gray-800 font-medium justify-between items-center w-full bg-white flex">
                {/* Left Column */}
                <div className="flex gap-2 items-center">
                    {icon}
                    <div className="flex flex-col">
                        <div className="text-gray-800 text-base">{title}</div>
                        <div className="flex flex-wrap items-center text-sm text-gray-500 gap-1">
                            <div className="flex-none">{formattedDate} · </div>
                            <div className="flex-none">{method} · </div>
                            <span
                                className="tag-1 truncate max-w-[150px]"
                            >
                                {category}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="ml-2">
                    <div className="whitespace-nowrap">{formattedAmount}</div>
                    <div className="text-sm text-gray-500 text-right">{getDaysUntil(date)} days</div>
                </div>
            </div>
        ) : (
            <div className="gap-2 p-2 font-medium hover:bg-gray-100 cursor-pointer text-base grid grid-cols-[2fr_1fr_1fr_1fr_1fr_2fr_auto] gap-6 items-center bg-white w-full rounded-md">

                {/* Title and Details */}
                <div className="flex gap-2 items-center">
                    {icon}
                    <div className="flex flex-col">
                        <div className="text-gray-800 font-semibold">{title}</div>
                        <div className="flex flex-wrap items-center text-sm text-gray-500 gap-1">
                            <span>{formattedDate} ● </span>
                            <span>{method}</span>
                        </div>
                    </div>
                </div>

                {/* Amount */}
                <div className="text-gray-800 whitespace-nowrap">
                    {formattedAmount}
                </div>

                {/* Account */}
                <div className="text-gray-600 whitespace-nowrap">
                    -
                </div>

                {/* Due in */}
                <div className="text-gray-800 whitespace-nowrap">
                    {getDaysUntil(date)} days
                </div>

                {/* Category */}
                <div
                    className={`text-sm w-fit px-3 py-1 rounded-full mt-1`}
                >
                    {category}
                </div>

                {/* Category */}
                <div
                    className={`tag-1 w-fit`}
                >
                    {category}
                </div>

                <div className="w-5 h-5" />
            </div>
        )
    );
};

export default PayableCard;
